import { useState, useEffect } from 'react';
import { Box, Typography, Card, CardContent, CircularProgress, Alert } from '@mui/material';
import AssignmentIcon from '@mui/icons-material/Assignment';
import AppLayout from '../components/AppLayout';
import { apiService } from '../services/api';
import { API_ENDPOINTS } from '../constants/api';
import { genericCopy } from '../utils/userFacingCopy';
import { formatDate } from '../utils/dateFormat';
import { useAuth } from '../contexts/AuthContext';

export default function GovernancePage() {
  const { displayTimeZoneId } = useAuth();
  const [governanceWording, setGovernanceWording] = useState<string | null>(null);
  const [governanceLastAcknowledgedAt, setGovernanceLastAcknowledgedAt] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string>('');

  useEffect(() => {
    const loadGovernance = async () => {
      try {
        setLoading(true);
        setError('');
        const govResponse = await apiService.get<{
          mustAcknowledge: boolean;
          currentWording?: string;
          lastAcknowledgedAt?: string;
        }>(API_ENDPOINTS.USERS.GOVERNANCE_STATUS);
        setGovernanceWording(govResponse.data?.currentWording ?? null);
        setGovernanceLastAcknowledgedAt(govResponse.data?.lastAcknowledgedAt ?? null);
      } catch {
        setGovernanceWording(null);
        setGovernanceLastAcknowledgedAt(null);
        setError(genericCopy.governance);
      } finally {
        setLoading(false);
      }
    };
    loadGovernance();
  }, []);

  return (
    <AppLayout title="Governance & Compliance">
      <Box sx={{ maxWidth: 800, mx: 'auto', py: 3, px: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
          <AssignmentIcon sx={{ fontSize: 32, color: 'primary.main' }} />
          <Typography variant="h4" component="h1" fontWeight="bold">
            Governance & Compliance
          </Typography>
        </Box>

        <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
          View the current data processing and compliance wording you have acknowledged. When the organisation updates this text or a new compliance period starts, you will be asked to acknowledge it again after login.
        </Typography>

        {error && (
          <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError('')}>
            {error}
          </Alert>
        )}

        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress />
          </Box>
        ) : (
          <Card sx={{ boxShadow: '0 2px 8px rgba(0,0,0,0.1)' }}>
            <CardContent sx={{ p: 3 }}>
              {governanceWording ? (
                <Typography variant="body1" component="div" sx={{ whiteSpace: 'pre-wrap' }}>
                  {governanceWording}
                </Typography>
              ) : (
                <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                  No governance wording is currently configured.
                </Typography>
              )}
              {governanceLastAcknowledgedAt && (
                <Typography variant="body2" color="text.secondary" sx={{ mt: 3, pt: 2, borderTop: '1px solid', borderColor: 'divider' }} key={displayTimeZoneId}>
                  You acknowledged this on {formatDate(governanceLastAcknowledgedAt, { includeTime: true })}.
                </Typography>
              )}
            </CardContent>
          </Card>
        )}
      </Box>
    </AppLayout>
  );
}
